import _ from 'lodash';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { Spinner } from '~/components';
import getGradientColor from '~/routes/app/pipeline/opportunities/components/gradient';
import { colors } from '~/styles';

const Container = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  padding: 1rem 0.75rem;
`;

const ProgressContent = styled.div`
  flex: 1;
`;

const ProgressHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;
`;

const ProgressName = styled.span`
  display: flex;
  align-items: center;
`;

const ProgressValue = styled.span`
  font-size: 0.875rem;
`;

const BarContainer = styled.div`
  width: 100%;
  padding-top: 0.1rem;
`;

const Meter = styled.div`
  display: flex;
  min-height: 0.75rem;
  background: ${colors.white};
  border: 1px solid ${colors.grey10};
  position: relative;
  border-radius: 0.5rem;
  padding: 0.125rem;
  min-width: 100%;
  overflow: hidden;
`;

const InsideBar = styled.div`
  min-width: 0.1rem;
  border-radius: 0.25rem;
  width: ${({ width }) => width};
  background: ${({ color }) => color};
  transition: width 0.5s linear;
`;

export default function SyncStatusBar({ sync }) {
  if (!sync) {
    sync = { statusId: 'never-run' };
  }
  const steps = [
    { id: 'pending', name: 'Pending', percentage: 0 },
    { id: 'initializing', name: 'Initializing', percentage: 10 },
    { id: 'companies', name: 'Syncing Salesforce Accounts', percentage: 25 },
    { id: 'contacts', name: 'Syncing Salesforce Contacts', percentage: 40 },
    { id: 'pipelines', name: 'Syncing Salesforce Sales Processes', percentage: 55 },
    { id: 'stages', name: 'Syncing Salesforce Opportunity Stages', percentage: 70 },
    { id: 'opportunities', name: 'Syncing Salesforce Opportunities', percentage: 85 },
    { id: 'activities', name: 'Syncing Salesforce Tasks and Events', percentage: 95 },
    { id: 'completed', name: 'Completed', percentage: 100 },
    { id: 'failed', name: 'Failed', percentage: 0 },
    { id: 'never-run', name: 'Never Been Run', percentage: 0 },
  ];

  const currentStep = steps.find((step) => step.id === sync?.statusId) || steps[0];
  const isCompleted = sync?.statusId === 'completed';
  const isFailed = sync?.statusId === 'failed';
  const isNeverRun = sync?.statusId === 'never-run';

  const [progress, setProgress] = useState(0);
  useEffect(() => {
    if (isFailed) {
      setProgress(0);
      return;
    }
    const target = isCompleted ? 100 : currentStep.percentage;
    if (!sync?.isRunning && progress === 0) {
      setProgress(target);
      return;
    }
    if (progress < target) {
      const timer = setTimeout(() => setProgress(progress + 1), 30);
      return () => clearTimeout(timer);
    } else if (progress > target) {
      setProgress(target);
    }
  }, [progress, currentStep.percentage, isCompleted, isFailed, sync?.isRunning]);

  // Derive the displayed step from the animated progress rather than the polled
  // statusId. Fast steps (e.g. pipelines) only hold their status for a fraction of
  // a second, so the 2s poll usually misses them — but the bar still animates past
  // their percentage, so following progress guarantees every step label is shown.
  const displayStep =
    isFailed || isNeverRun
      ? currentStep
      : (_.findLast(steps, (step) => step.percentage > 0 && step.percentage <= progress) ?? currentStep);

  const probabilityColor = isCompleted
    ? colors.success
    : isFailed
      ? colors.danger
      : getGradientColor(currentStep.percentage);

  return (
    <Container>
      <ProgressContent>
        <ProgressHeader>
          <span>
            {isCompleted && progress === 100 ? (
              <div>{sync.fullSync ? 'Full' : 'Incremental'} Sync Completed</div>
            ) : (
              <ProgressName>
                <div style={{ marginRight: '0.5rem' }}>{displayStep.name}</div>
                {progress < 100 && !isFailed && !isNeverRun && <Spinner />}
              </ProgressName>
            )}
          </span>
          <ProgressValue>{progress === 100 ? moment(sync.endedAt).fromNow() : `${progress}%`}</ProgressValue>
        </ProgressHeader>
        <BarContainer>
          <Meter>
            <InsideBar width={`${progress}%`} color={probabilityColor} />
          </Meter>
        </BarContainer>
      </ProgressContent>
    </Container>
  );
}
